class TelehealthAudioManager {
  constructor() {
    this.microphone = null;
    this.preferredMicrophone = null;
    this.activeMicrophoneId = "";
    this.screenAudio = null;
    this.videoTrack = null;
    this.displayStream = null;
    this.listeners = new Set();
    this.isActive = false;
    this.audioContext = null;
    this.mixDestination = null;
    this.microphoneSource = null;
    this.screenAudioSource = null;
    this.microphoneGain = null;
    this.screenAudioGain = null;
    this.microphoneGeneration = 0;
    this.screenAudioGeneration = 0;
    this.deviceChangeTimeout = null;
    this.deviceChangeHandler = null;
    this.keepaliveAudioEl = null;
  }

  // ✅ Subscribe to changes
  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback); // Return unsubscribe function
  }

  // ✅ Notify all listeners of changes
  notify() {
    const state = this.getState();
    this.listeners.forEach((callback) => callback(state));
  }

  // ✅ Get current state without subscribing
  getState() {
    return {
      hasMicrophone: !!this.microphone,
      hasScreenAudio: !!this.screenAudio,
      preferredMicrophone: this.preferredMicrophone,
      activeMicrophoneId: this.activeMicrophoneId,
      isUsingFallback:
        Boolean(this.preferredMicrophone?.deviceId) &&
        this.activeMicrophoneId !== this.preferredMicrophone.deviceId,
    };
  }

  getMicrophoneStream() {
    return this.microphone;
  }

  getScreenAudioStream() {
    return this.screenAudio;
  }

  ensureDeviceChangeListener() {
    if (this.deviceChangeHandler || !navigator.mediaDevices?.addEventListener) {
      return;
    }

    this.deviceChangeHandler = () => {
      if (this.deviceChangeTimeout) {
        clearTimeout(this.deviceChangeTimeout);
      }
      this.deviceChangeTimeout = setTimeout(() => {
        this.deviceChangeTimeout = null;
        void this.reconcileMicrophone("devicechange");
      }, 400);
    };
    navigator.mediaDevices.addEventListener("devicechange", this.deviceChangeHandler);
  }

  async reconcileMicrophone(reason = "unknown") {
    let microphones = [];
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      microphones = devices.filter((device) => device.kind === "audioinput");
    } catch (error) {
      console.warn("Unable to enumerate microphones during telehealth recovery", error);
    }

    const activeTrack = this.microphone?.getAudioTracks?.()[0];
    const activeIsLive = activeTrack?.readyState === "live";
    const activeStillExists =
      activeIsLive &&
      (!this.activeMicrophoneId ||
        microphones.length === 0 ||
        microphones.some((device) => device.deviceId === this.activeMicrophoneId));
    const preferredDevice = this.preferredMicrophone?.deviceId
      ? microphones.find((device) => device.deviceId === this.preferredMicrophone.deviceId)
      : null;

    if (preferredDevice && this.activeMicrophoneId !== preferredDevice.deviceId) {
      const result = await this.setupMicrophone(preferredDevice.deviceId, {
        updatePreference: false,
        reason: `${reason}-preferred-returned`,
      });
      if (result.success) {
        return result;
      }
    }

    if (activeStillExists) {
      return { success: true, track: activeTrack };
    }

    return this.setupMicrophone(null, {
      updatePreference: false,
      reason: `${reason}-fallback`,
    });
  }

  ensureMixer() {
    if (this.audioContext && this.mixDestination) {
      return;
    }

    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    this.audioContext = new AudioContextClass({ latencyHint: "interactive" });
    this.mixDestination = this.audioContext.createMediaStreamDestination();
    this.microphoneGain = this.audioContext.createGain();
    this.screenAudioGain = this.audioContext.createGain();
    this.microphoneGain.gain.value = 1;
    this.screenAudioGain.gain.value = 1;
    this.microphoneGain.connect(this.mixDestination);
    this.screenAudioGain.connect(this.mixDestination);

    // The mixer only feeds a MediaStreamDestination, so nothing "plays" it. When
    // Knowtex is minimized or loses focus (Webex/Edge CVT carts), the browser can
    // suspend this context and shared-tab patient audio silently drops out of the
    // recording while the mic path continues. A muted <audio> sink on the mixed
    // stream marks the context as actively consumed so it keeps rendering.
    if (typeof Audio !== "undefined") {
      this.keepaliveAudioEl = new Audio();
      this.keepaliveAudioEl.muted = true;
      this.keepaliveAudioEl.srcObject = this.mixDestination.stream;
    }
  }

  /** Resume a suspended mixer and (re)start the muted sink. No-op when already running. */
  async ensureMixerRunning() {
    if (!this.audioContext) {
      return;
    }
    if (this.audioContext.state === "suspended") {
      await this.audioContext.resume().catch((error) => {
        console.warn("Unable to resume telehealth mixer AudioContext", error);
      });
    }
    // play() rejects until a user gesture; recording start provides one.
    await this.keepaliveAudioEl?.play?.().catch(() => {});
  }

  replaceMixerMicrophone(stream) {
    this.ensureMixer();
    const nextSource = this.audioContext.createMediaStreamSource(stream);
    nextSource.connect(this.microphoneGain);
    const previousSource = this.microphoneSource;
    this.microphoneSource = nextSource;
    try {
      previousSource?.disconnect();
    } catch (error) {
      console.warn("Unable to disconnect previous telehealth microphone source", error);
    }
  }

  replaceMixerScreenAudio(stream) {
    this.ensureMixer();
    const nextSource = stream ? this.audioContext.createMediaStreamSource(stream) : null;
    nextSource?.connect(this.screenAudioGain);
    const previousSource = this.screenAudioSource;
    this.screenAudioSource = nextSource;
    try {
      previousSource?.disconnect();
    } catch (error) {
      console.warn("Unable to disconnect previous telehealth screen source", error);
    }
  }

  // Setup or hot-swap the provider microphone without interrupting the mixed output.
  async setupMicrophone(deviceId = null, options = {}) {
    const { updatePreference = Boolean(deviceId), reason = "manual" } = options;
    const generation = ++this.microphoneGeneration;
    this.ensureDeviceChangeListener();

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: deviceId ? { deviceId: { exact: deviceId } } : true,
        video: false,
      });

      const micTrack = stream.getAudioTracks()[0];
      if (!micTrack || micTrack.readyState !== "live") {
        stream.getTracks().forEach((track) => track.stop());
        throw new Error("The selected microphone did not provide a live audio track.");
      }

      if (generation !== this.microphoneGeneration) {
        stream.getTracks().forEach((track) => track.stop());
        return { success: false, stale: true, error: "A newer microphone selection won." };
      }

      const settings = micTrack.getSettings?.() || {};
      const resolvedDeviceId = settings.deviceId || deviceId || "";
      const previousMicrophone = this.microphone;

      this.replaceMixerMicrophone(stream);
      this.microphone = stream;
      this.activeMicrophoneId = resolvedDeviceId;
      if (updatePreference || !this.preferredMicrophone) {
        this.preferredMicrophone = {
          deviceId: resolvedDeviceId,
          label: micTrack.label || "",
          groupId: settings.groupId || "",
        };
      }

      console.log("🎤 Microphone connected:", {
        label: micTrack.label,
        readyState: micTrack.readyState,
        enabled: micTrack.enabled,
        reason,
      });

      micTrack.onended = async () => {
        console.error("❌ Microphone track ended unexpectedly");
        if (this.microphone !== stream) {
          return;
        }
        this.notify();
        await this.reconcileMicrophone("track-ended");
      };

      micTrack.onmute = () => console.warn("⚠️ Microphone muted");
      micTrack.onunmute = () => console.log("✅ Microphone unmuted");

      this.isActive = true;
      this.notify();
      if (previousMicrophone && previousMicrophone !== stream) {
        previousMicrophone.getTracks().forEach((track) => track.stop());
      }

      return { success: true, track: micTrack };
    } catch (err) {
      console.error("Microphone setup error:", err);
      return { success: false, error: err.message, errorName: err.name };
    }
  }

  // ✅ Setup screen audio (doesn't interfere with microphone)
  async setupScreenAudio() {
    // The share picker can stay open for a while. If telehealth is torn down
    // in the meantime (cleanup, or the tour closing setup), the stream it
    // eventually returns must not be attached, or the tab keeps sharing with
    // telehealth showing as off.
    const generation = ++this.screenAudioGeneration;
    try {
      // Stop existing screen audio if any
      if (this.screenAudio) {
        this.screenAudio.getTracks().forEach((track) => track.stop());
        this.screenAudio = null;
      }
      if (this.videoTrack) {
        this.videoTrack.stop();
        this.videoTrack = null;
      }
      if (this.displayStream) {
        this.displayStream.getTracks().forEach((track) => track.stop());
        this.displayStream = null;
      }

      const canControlFocus =
        typeof window !== "undefined" &&
        "CaptureController" in window &&
        "setFocusBehavior" in window.CaptureController.prototype;

      const mediaOptions = {
        video: true,
        audio: true,
      };

      if (canControlFocus) {
        const controller = new window.CaptureController();
        try {
          controller.setFocusBehavior("no-focus-change");
          console.debug("CaptureController focus behavior: no-focus-change");
        } catch (err) {
          console.warn("CaptureController focus behavior failed:", err);
        }
        mediaOptions.controller = controller;
      } else {
        console.debug("CaptureController not available; default focus behavior applies");
      }

      const displayStream = await navigator.mediaDevices.getDisplayMedia(mediaOptions);

      if (generation !== this.screenAudioGeneration) {
        displayStream.getTracks().forEach((track) => track.stop());
        return { success: false, stale: true, error: "Screen share was cancelled." };
      }

      const audioTracks = displayStream.getAudioTracks();
      const videoTracks = displayStream.getVideoTracks();

      if (audioTracks.length === 0) {
        displayStream.getTracks().forEach((track) => track.stop());
        return {
          success: false,
          error: "No audio detected. Make sure to check 'Share audio' when selecting your tab.",
        };
      }

      const screenAudioTrack = audioTracks[0];
      const videoTrack = videoTracks[0];

      // ✅ Store the ENTIRE display stream so we can stop it all later
      this.displayStream = displayStream;
      this.screenAudio = new MediaStream([screenAudioTrack]);
      this.videoTrack = videoTrack;
      this.replaceMixerScreenAudio(this.screenAudio);

      // ✅ Add event listeners - if user manually stops sharing, stop screen audio only
      screenAudioTrack.onended = () => this.stopScreenAudio();
      videoTrack.addEventListener("ended", () => this.stopScreenAudio());

      this.notify();
      return { success: true, track: screenAudioTrack };
    } catch (err) {
      return { success: false, error: err.message, errorName: err.name };
    }
  }

  // ✅ Get combined stream for recording (SIMPLIFIED - backend handles mono conversion)
  getCombinedStream() {
    if (!this.microphone) {
      console.error("❌ No microphone available");
      return null;
    }

    const micTrack = this.microphone.getAudioTracks()[0];
    if (!micTrack || micTrack.readyState !== "live") {
      console.error("❌ Microphone track is not live");
      return null;
    }

    try {
      this.ensureMixer();
      if (!this.microphoneSource) {
        this.replaceMixerMicrophone(this.microphone);
      }
      if (this.screenAudio && !this.screenAudioSource) {
        this.replaceMixerScreenAudio(this.screenAudio);
      }
      void this.ensureMixerRunning();
      return this.mixDestination.stream;
    } catch (err) {
      console.error("❌ Error creating combined stream:", err);
      return this.microphone; // Fallback to mic only
    }
  }

  // ✅ Cleanup all sources
  cleanup() {
    this.microphoneGeneration += 1;
    this.screenAudioGeneration += 1;
    if (this.deviceChangeTimeout) {
      clearTimeout(this.deviceChangeTimeout);
      this.deviceChangeTimeout = null;
    }
    if (this.deviceChangeHandler && navigator.mediaDevices?.removeEventListener) {
      navigator.mediaDevices.removeEventListener("devicechange", this.deviceChangeHandler);
      this.deviceChangeHandler = null;
    }
    // Stop microphone
    if (this.microphone) {
      this.microphone.getTracks().forEach((track) => track.stop());
      this.microphone = null;
    }
    this.activeMicrophoneId = "";
    this.preferredMicrophone = null;

    this.stopScreenAudio(false);

    if (this.keepaliveAudioEl) {
      this.keepaliveAudioEl.pause?.();
      this.keepaliveAudioEl.srcObject = null;
      this.keepaliveAudioEl = null;
    }

    try {
      this.microphoneSource?.disconnect();
      this.screenAudioSource?.disconnect();
      this.microphoneGain?.disconnect();
      this.screenAudioGain?.disconnect();
    } catch (error) {
      console.warn("Unable to disconnect telehealth mixer", error);
    }
    if (this.audioContext) {
      void this.audioContext.close();
    }
    this.audioContext = null;
    this.mixDestination = null;
    this.microphoneSource = null;
    this.screenAudioSource = null;
    this.microphoneGain = null;
    this.screenAudioGain = null;

    this.isActive = false;
    this.notify();
  }

  // ✅ Stop only screen audio + display stream
  stopScreenAudio(shouldNotify = true) {
    if (this.screenAudio) {
      this.screenAudio.getTracks().forEach((track) => track.stop());
      this.screenAudio = null;
    }

    if (this.videoTrack) {
      this.videoTrack.stop();
      this.videoTrack = null;
    }

    if (this.displayStream) {
      this.displayStream.getTracks().forEach((track) => track.stop());
      this.displayStream = null;
    }
    if (this.audioContext && this.mixDestination) {
      this.replaceMixerScreenAudio(null);
    }

    if (shouldNotify) {
      this.notify();
    }
  }

  // ✅ Check if both sources are ready
  isReady() {
    return !!(this.microphone && this.screenAudio);
  }

  // ✅ Get available microphones
  static async getAvailableMicrophones() {
    try {
      // Request permission first
      const tempStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      tempStream.getTracks().forEach((track) => track.stop());

      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices.filter((device) => device.kind === "audioinput");
    } catch (err) {
      console.error("Error getting audio devices:", err);
      return [];
    }
  }

  // ✅ Add this method to test recording quality
  async testCombinedStreamRecording() {
    const stream = this.getCombinedStream();

    if (!stream) {
      console.error("❌ Cannot test - no combined stream available");
      return null;
    }

    // Try recording a 2-second sample
    const testRecorder = new MediaRecorder(stream, {
      mimeType: "audio/webm;codecs=opus",
      audioBitsPerSecond: 128000,
    });

    const chunks = [];

    return new Promise((resolve) => {
      testRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      testRecorder.onstop = async () => {
        const testBlob = new Blob(chunks, { type: "audio/webm" });

        // Analyze the test recording
        const arrayBuffer = await testBlob.arrayBuffer();
        const bytes = new Uint8Array(arrayBuffer);

        const signature = Array.from(bytes.slice(0, 16))
          .map((b) => b.toString(16).padStart(2, "0"))
          .join(" ");

        // console.log("🧪 Test recording results:", {
        //   size: testBlob.size,
        //   sizeKB: (testBlob.size / 1024).toFixed(2),
        //   type: testBlob.type,
        //   signature: signature,
        //   isValidWebM: signature.includes('1a 45 df a3'),
        // });

        // Try to play it
        const audioURL = URL.createObjectURL(testBlob);
        const testAudio = new Audio(audioURL);

        testAudio.addEventListener("loadedmetadata", () => {
          console.log("✅ Test recording playable:", {
            duration: testAudio.duration,
            hasAudio: testAudio.duration > 0,
          });
        });

        testAudio.addEventListener("error", (err) => {
          console.error("❌ Test recording not playable:", err);
        });

        setTimeout(() => URL.revokeObjectURL(audioURL), 5000);

        resolve({
          blob: testBlob,
          size: testBlob.size,
          isValid: signature.includes("1a 45 df a3"),
        });
      };

      testRecorder.start();

      setTimeout(() => {
        testRecorder.stop();
      }, 2000);
    });
  }
}

// ✅ Export singleton instance
export default new TelehealthAudioManager();
